import { getName } from "@rentiohq/shared-frontend/dist/redux/contact/contact.utils";
import { ITemplate } from "@rentiohq/shared-frontend/dist/redux/template/template.types";
import { ITemplateDocument } from "@rentiohq/shared-frontend/dist/redux/templateDocument/templateDocument.types";
import { IBankAccount } from "@rentiohq/shared-frontend/dist/reduxV2/bankAccount";
import { IIndexInfoSimulation } from "@rentiohq/shared-frontend/dist/reduxV2/contract/contract.api";
import { PROPERTY_TYPES } from "@rentiohq/shared-frontend/dist/reduxV2/property/property.utils";
import {
  AMENITY_NAMES,
  getAmenityBooleanValue,
  getAmenityNumberValue,
  getAmenityStringValue,
} from "@rentiohq/shared-frontend/dist/reduxV2/propertyAmenity";
import { IBroker } from "@rentiohq/shared-frontend/dist/types/broker.types";
import { IContact } from "@rentiohq/shared-frontend/dist/types/contact.types";
import {
  EContractCostsPriceType,
  EContractIbanisationStatus,
  EContractMemberTypes,
  EEpcBasedIndexationType,
  IContract,
  IIndexInfoAvailable,
} from "@rentiohq/shared-frontend/dist/types/contract.types";
import { IPaymentOrder } from "@rentiohq/shared-frontend/dist/types/payment.types";
import {
  EPropertyEPCLabel,
  EPropertyMemberTypes,
  IProperty,
} from "@rentiohq/shared-frontend/dist/types/property.types";
import { IPropertyAmenity } from "@rentiohq/shared-frontend/dist/types/propertyAmenity.types";
import { formatAddress } from "@rentiohq/shared-frontend/dist/utils/address.utils";
import {
  addDays,
  differenceInDays,
} from "@rentiohq/shared-frontend/dist/utils/date-fns.utils";
import { getLocalizedText } from "@rentiohq/shared-frontend/dist/utils/i18n/i18n.utils";
import { formatIban } from "@rentiohq/shared-frontend/dist/utils/iban.utils";
import { getAmountWithVat } from "@rentiohq/shared-frontend/dist/utils/number.utils";
import {
  getMembersWithOneOfRoles,
  getMembersWithRole,
} from "@rentiohq/shared-frontend/dist/utils/roles.utils";
import { IPreferences } from "@rentiohq/web-shared/dist/redux/system/system.types";
import { EDefaultVariableId } from "@rentiohq/web-shared/dist/scenes/TemplateEditor/components/Editor/Editor.config";
import {
  IDocumentConfig,
  IFields,
} from "@rentiohq/web-shared/dist/scenes/TemplateEditor/components/Editor/Editor.types";
import { floor, round } from "lodash";

export const getDefaultFilename = (params: {
  type: "contract" | string;
  template?: ITemplate;
  templateDocument?: ITemplateDocument;
  property: IProperty;
}) => {
  const { type, template, templateDocument, property } = params;

  if (type === "contract") {
    return getLocalizedText(
      "create_template_document.modal.name_template.default_filename_contract",
      {
        address: formatAddress(property),
      },
    );
  }

  return template?.name || templateDocument?.name || "File";
};

export const getDefaultDocumentConfig = (params: { brandColor?: string }) => {
  const { brandColor } = params;

  const result: IDocumentConfig = {
    brandColor,
  };

  return JSON.stringify(result);
};

export const getDefaultVariablesData = (params: {
  template?: ITemplate;
  legalContact?: IContact;
  meMaster?: IContact;
  broker?: IBroker;
  brokerLogoDocumentId?: string;
  property: IProperty;
  propertyAmenities?: IPropertyAmenity[];
  contract?: IContract;
  indexInfoAvailable?: IIndexInfoAvailable;
  indexInfoSimulation?: IIndexInfoSimulation;
  indexInfoRequest?: {
    newPrice: number;
    isRetroactive: boolean;
    forceFullIndexation: boolean;
  };
  contacts: IContact[];
  fields: IFields;
  preferences: IPreferences;
  rentPaymentOrder?: IPaymentOrder;

  ibanisationBankAccount?: IBankAccount;
  ownerBankAccount?: IBankAccount;
  variablesDataToOverride?: Partial<
    Record<
      EDefaultVariableId,
      (string | number | boolean | Date | undefined | null)[]
    >
  >;
}) => {
  const {
    legalContact,
    meMaster,
    broker,
    brokerLogoDocumentId,
    property,
    propertyAmenities,
    contract,
    indexInfoAvailable,
    indexInfoSimulation,
    indexInfoRequest,
    contacts,
    fields,
    preferences,
    rentPaymentOrder,
    ibanisationBankAccount,
    variablesDataToOverride,
    ownerBankAccount,
    template,
  } = params;

  const translateByTemplateLanguage = (key: string) =>
    getLocalizedText(key, undefined, undefined, template?.locale);

  const result: Record<
    EDefaultVariableId,
    (string | number | boolean | Date | undefined | null)[]
  > = {
    [EDefaultVariableId.Now]: [new Date()],
    [EDefaultVariableId.CreatedAt]: [new Date()],
    [EDefaultVariableId.RentDepositPrice]: [],
    [EDefaultVariableId.RentDepositMonths]: [],
    [EDefaultVariableId.RentDepositWithRentio]: [],
    [EDefaultVariableId.RentDepositBankAccountNumber]: [],
    [EDefaultVariableId.LocationDescriptionPerson]: [],
    [EDefaultVariableId.BrokerEnabled]: [],
    [EDefaultVariableId.BrokerBiv]: [],
    [EDefaultVariableId.BrokerOfficeName]: [],
    [EDefaultVariableId.BrokerFullName]: [],
    [EDefaultVariableId.BrokerFirstName]: [],
    [EDefaultVariableId.BrokerLastName]: [],
    [EDefaultVariableId.BrokerBirthPlace]: [],
    [EDefaultVariableId.BrokerBirthDate]: [],
    [EDefaultVariableId.BrokerAddressFull]: [],
    [EDefaultVariableId.BrokerAddressStreet]: [],
    [EDefaultVariableId.BrokerAddressNumber]: [],
    [EDefaultVariableId.BrokerAddressBox]: [],
    [EDefaultVariableId.BrokerAddressZip]: [],
    [EDefaultVariableId.BrokerAddressCity]: [],
    [EDefaultVariableId.BrokerAddressCountry]: [],
    [EDefaultVariableId.BrokerLogo]: [],
    [EDefaultVariableId.BrokerIsSignerOnBehalfOfOwners]: [],
    [EDefaultVariableId.BrokerSignature]: [],
    [EDefaultVariableId.BrokerCivilLiabilityAtCompany]: [],
    [EDefaultVariableId.BrokerCivilLiabilityPolicyNumber]: [],
    [EDefaultVariableId.OwnerIsCompany]: [],
    [EDefaultVariableId.OwnerFullName]: [],
    [EDefaultVariableId.OwnerFirstName]: [],
    [EDefaultVariableId.OwnerLastName]: [],
    [EDefaultVariableId.OwnerBirthPlace]: [],
    [EDefaultVariableId.OwnerBirthDate]: [],
    [EDefaultVariableId.OwnerCompanyName]: [],
    [EDefaultVariableId.OwnerCompanyVat]: [],
    [EDefaultVariableId.OwnerCompanyRole]: [],
    [EDefaultVariableId.OwnerAddressFull]: [],
    [EDefaultVariableId.OwnerAddressStreet]: [],
    [EDefaultVariableId.OwnerAddressNumber]: [],
    [EDefaultVariableId.OwnerAddressBox]: [],
    [EDefaultVariableId.OwnerAddressZip]: [],
    [EDefaultVariableId.OwnerAddressCity]: [],
    [EDefaultVariableId.OwnerAddressCountry]: [],
    [EDefaultVariableId.OwnerCompanyAddressFull]: [],
    [EDefaultVariableId.OwnerSignature]: [],
    [EDefaultVariableId.OwnerEmail]: [],
    [EDefaultVariableId.OwnerPhone]: [],
    [EDefaultVariableId.OwnerNationality]: [],
    [EDefaultVariableId.OwnerNationalRegisterNumber]: [],
    [EDefaultVariableId.OwnerPassportNumber]: [],
    [EDefaultVariableId.OwnerMaritalStatus]: [],
    [EDefaultVariableId.OwnerBankAccountNumber]: [],
    [EDefaultVariableId.TenantIsCompany]: [],
    [EDefaultVariableId.TenantFullName]: [],
    [EDefaultVariableId.TenantFirstName]: [],
    [EDefaultVariableId.TenantLastName]: [],
    [EDefaultVariableId.TenantBirthPlace]: [],
    [EDefaultVariableId.TenantBirthDate]: [],
    [EDefaultVariableId.TenantCompanyName]: [],
    [EDefaultVariableId.TenantCompanyVat]: [],
    [EDefaultVariableId.TenantCompanyRole]: [],
    [EDefaultVariableId.TenantAddressFull]: [],
    [EDefaultVariableId.TenantAddressStreet]: [],
    [EDefaultVariableId.TenantAddressNumber]: [],
    [EDefaultVariableId.TenantAddressBox]: [],
    [EDefaultVariableId.TenantAddressZip]: [],
    [EDefaultVariableId.TenantAddressCity]: [],
    [EDefaultVariableId.TenantAddressCountry]: [],
    [EDefaultVariableId.TenantCompanyAddressFull]: [],
    [EDefaultVariableId.TenantSignature]: [],
    [EDefaultVariableId.TenantEmail]: [],
    [EDefaultVariableId.TenantPhone]: [],
    [EDefaultVariableId.TenantNationality]: [],
    [EDefaultVariableId.TenantNationalRegisterNumber]: [],
    [EDefaultVariableId.TenantPassportNumber]: [],
    [EDefaultVariableId.TenantMaritalStatus]: [],
    [EDefaultVariableId.ParentIsCompany]: [],
    [EDefaultVariableId.ParentFullName]: [],
    [EDefaultVariableId.ParentFirstName]: [],
    [EDefaultVariableId.ParentLastName]: [],
    [EDefaultVariableId.ParentBirthPlace]: [],
    [EDefaultVariableId.ParentBirthDate]: [],
    [EDefaultVariableId.ParentCompanyName]: [],
    [EDefaultVariableId.ParentCompanyVat]: [],
    [EDefaultVariableId.ParentCompanyRole]: [],
    [EDefaultVariableId.ParentAddressFull]: [],
    [EDefaultVariableId.ParentAddressStreet]: [],
    [EDefaultVariableId.ParentAddressNumber]: [],
    [EDefaultVariableId.ParentAddressBox]: [],
    [EDefaultVariableId.ParentAddressZip]: [],
    [EDefaultVariableId.ParentAddressCity]: [],
    [EDefaultVariableId.ParentAddressCountry]: [],
    [EDefaultVariableId.ParentCompanyAddressFull]: [],
    [EDefaultVariableId.ParentSignature]: [],
    [EDefaultVariableId.ParentEmail]: [],
    [EDefaultVariableId.ParentPhone]: [],
    [EDefaultVariableId.ParentNationality]: [],
    [EDefaultVariableId.ParentNationalRegisterNumber]: [],
    [EDefaultVariableId.ParentPassportNumber]: [],
    [EDefaultVariableId.ParentMaritalStatus]: [],
    [EDefaultVariableId.PropertyType]: [],
    [EDefaultVariableId.PropertyAddressFull]: [],
    [EDefaultVariableId.PropertyAddressStreet]: [],
    [EDefaultVariableId.PropertyAddressNumber]: [],
    [EDefaultVariableId.PropertyAddressBox]: [],
    [EDefaultVariableId.PropertyAddressZip]: [],
    [EDefaultVariableId.PropertyAddressCity]: [],
    [EDefaultVariableId.PropertyAddressCountry]: [],
    [EDefaultVariableId.PropertyDescription]: [],
    [EDefaultVariableId.PropertyName]: [],
    [EDefaultVariableId.PropertyReference]: [],
    [EDefaultVariableId.PropertySyndic]: [],
    [EDefaultVariableId.PropertyHasOilTank]: [],
    [EDefaultVariableId.PropertyOilTankIsBuried]: [],
    [EDefaultVariableId.PropertyOilTankVolume]: [],
    [EDefaultVariableId.PropertySmokeDetectorCount]: [],
    [EDefaultVariableId.PropertyEpcLabel]: [],
    [EDefaultVariableId.PropertyEpcValue]: [],
    [EDefaultVariableId.PropertyEpcExpirationDate]: [],
    [EDefaultVariableId.PropertyUtilityCounterWaterAvailable]: [],
    [EDefaultVariableId.PropertyUtilityCounterWaterIsCollective]: [],
    [EDefaultVariableId.PropertyUtilityCounterNaturalGasAvailable]: [],
    [EDefaultVariableId.PropertyUtilityCounterNaturalGasIsCollective]: [],
    [EDefaultVariableId.PropertyUtilityCounterElectricityAvailable]: [],
    [EDefaultVariableId.PropertyUtilityCounterElectricityIsCollective]: [],
    [EDefaultVariableId.PropertyUsagePrivatePercentage]: [],
    [EDefaultVariableId.PropertyUsageProfessionalPercentage]: [],
    [EDefaultVariableId.PropertyTechnicalManagers]: [],
    [EDefaultVariableId.PropertyFinancialManagers]: [],
    [EDefaultVariableId.PropertyConstructionYear]: [],
    [EDefaultVariableId.PropertyElectricityType]: [],
    [EDefaultVariableId.PropertyElectricityMeterType]: [],
    [EDefaultVariableId.PropertyNaturalGasAvailable]: [],
    [EDefaultVariableId.PropertyNaturalGasMeterType]: [],
    [EDefaultVariableId.PropertyEpcCertificateId]: [],
    [EDefaultVariableId.PropertyBuildingAdjoined]: [],
    [EDefaultVariableId.PropertyBuildingSurfaceArea]: [],
    [EDefaultVariableId.PropertyBuildingGardenSurfaceArea]: [],
    [EDefaultVariableId.PropertyBuildingTerraceSurfaceArea]: [],
    [EDefaultVariableId.PropertyBuildingFloor]: [],
    [EDefaultVariableId.PropertyBuildingFloorCount]: [],
    [EDefaultVariableId.PropertyRoomCountBedroom]: [],
    [EDefaultVariableId.PropertyRoomCountBathroom]: [],
    [EDefaultVariableId.PropertyRoomCountGarage]: [],
    [EDefaultVariableId.PropertyBuildingHasStorage]: [],
    [EDefaultVariableId.PropertyIsFurnished]: [],
    [EDefaultVariableId.PropertyHasOven]: [],
    [EDefaultVariableId.PropertyHasFridge]: [],
    [EDefaultVariableId.PropertyHasDishwasher]: [],
    [EDefaultVariableId.PropertyHasRangeHood]: [],
    [EDefaultVariableId.PropertyHasLift]: [],
    [EDefaultVariableId.PropertyHasDoubleGlazing]: [],
    [EDefaultVariableId.PropertyHasCentralHeating]: [],
    [EDefaultVariableId.PropertyHeatingType]: [],
    [EDefaultVariableId.ContractStartDate]: [],
    [EDefaultVariableId.ContractStopDate]: [],
    [EDefaultVariableId.ContractPaymentRepetition]: [],
    [EDefaultVariableId.ContractPriceVat]: [],
    [EDefaultVariableId.ContractStartPrice]: [],
    [EDefaultVariableId.ContractStartPriceIncludingVat]: [],
    [EDefaultVariableId.ContractCostsCommonVat]: [],
    [EDefaultVariableId.ContractStartCostsCommonFixed]: [],
    [EDefaultVariableId.ContractStartCostsCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractStartCostsCommonProvision]: [],
    [EDefaultVariableId.ContractStartCostsCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractCostsNonCommonVat]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonFixed]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonProvision]: [],
    [EDefaultVariableId.ContractStartCostsNonCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentPrice]: [],
    [EDefaultVariableId.ContractCurrentPriceIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonFixed]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonProvision]: [],
    [EDefaultVariableId.ContractCurrentCostsCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonFixed]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonFixedIncludingVat]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonProvision]: [],
    [EDefaultVariableId.ContractCurrentCostsNonCommonProvisionIncludingVat]: [],
    [EDefaultVariableId.ContractPriceTypeCostsCommon]: [],
    [EDefaultVariableId.ContractPriceTypeCostsNonCommon]: [],
    [EDefaultVariableId.ContractStartIndexDate]: [],
    [EDefaultVariableId.ContractStartIndexNumber]: [],
    [EDefaultVariableId.ContractDurationYears]: [],
    [EDefaultVariableId.ContractIndexStartIndex]: [],
    [EDefaultVariableId.ContractIndexNewIndex]: [],
    [EDefaultVariableId.ContractIndexCorrectionFactor]: [],
    [EDefaultVariableId.ContractIndexEpcBasedIndexType]: [],
    [EDefaultVariableId.ContractIndexStartNextPaymentPeriod]: [],
    [EDefaultVariableId.ContractIndexNewPriceWasAdjusted]: [],
    [EDefaultVariableId.ContractIndexNewPriceOriginal]: [],
    [EDefaultVariableId.ContractIndexNewCommonCostsOriginal]: [],
    [EDefaultVariableId.ContractIndexNewNonCommonCostsOriginal]: [],
    [EDefaultVariableId.ContractIndexNewPriceAdjusted]: [],
    [EDefaultVariableId.ContractIndexNewCommonCostsAdjusted]: [],
    [EDefaultVariableId.ContractIndexNewNonCommonCostsAdjusted]: [],
    [EDefaultVariableId.ContractIndexTotalPriceOriginal]: [],
    [EDefaultVariableId.ContractIndexTotalPriceAdjusted]: [],
    [EDefaultVariableId.ContractIndexForceFullIndexation]: [],
    [EDefaultVariableId.ContractRetroactivityPeriodStart]: [],
    [EDefaultVariableId.ContractRetroactivityPeriodEnd]: [],
    [EDefaultVariableId.ContractRetroactivityPeriodPrice]: [],
    [EDefaultVariableId.ContractIndexPaymentsWithRentio]: [],
    [EDefaultVariableId.ContractIndexPaymentOrderNewDueDate]: [],
    [EDefaultVariableId.ContractIbanisationBankAccountIban]: [],
    [EDefaultVariableId.ContractIbanisationBankAccountBic]: [],
    [EDefaultVariableId.ContractIbanisationBankAccountReference]: [],
    [EDefaultVariableId.ContractIndexCorrectionPeriodStart]: [],
    [EDefaultVariableId.ContractIndexCorrectionPeriodEnd]: [],
    [EDefaultVariableId.ContractIndexCorrectionPeriodPrice]: [],
    [EDefaultVariableId.ContractIndexCorrectionAndRetroSum]: [],
    [EDefaultVariableId.ContractIndexRetroactivePayments]: [],
    [EDefaultVariableId.EanCodeElectricity]: [],
    [EDefaultVariableId.EanCodeGas]: [],
    [EDefaultVariableId.MeterNumberGas]: [],
    [EDefaultVariableId.MeterNumberElectricity]: [],
    [EDefaultVariableId.MeterNumberWater]: [],
    [EDefaultVariableId.AdditionalCostDescription]: [],
    [EDefaultVariableId.AdditionalCostStartPrice]: [],
    [EDefaultVariableId.AdditionalCostCorrectionFactor]: [],
    [EDefaultVariableId.AdditionalCostProposedIncrease]: [],
    [EDefaultVariableId.AdditionalCostNewPrice]: [],
  };

  const defaultVariableIds = Object.values(EDefaultVariableId) as string[];

  fields.variables.forEach(variable => {
    if (defaultVariableIds.includes(variable.id)) {
      return;
    }

    result[variable.id as EDefaultVariableId] = [
      variable.defaultDate ||
        variable.defaultNumber ||
        variable.defaultText ||
        variable.defaultToggle,
    ];
  });

  // TODO: Legal or financial contact?
  // Broker (legal contact & broker objects)
  if (legalContact) {
    result[EDefaultVariableId.BrokerEnabled] = [true];
    result[EDefaultVariableId.BrokerBiv] = [legalContact.bivNumber];
    result[EDefaultVariableId.BrokerFullName] = [
      [legalContact.firstname, legalContact.lastname].filter(Boolean).join(" "),
    ];
    result[EDefaultVariableId.BrokerFirstName] = [legalContact.firstname];
    result[EDefaultVariableId.BrokerLastName] = [legalContact.lastname];
    result[EDefaultVariableId.BrokerBirthPlace] = [legalContact.placeOfBirth];
    result[EDefaultVariableId.BrokerBirthDate] = [legalContact.dateOfBirth];
    result[EDefaultVariableId.BrokerAddressFull] = [
      formatAddress(legalContact, true, true, true, true),
    ];
    result[EDefaultVariableId.BrokerAddressStreet] = [legalContact.street];
    result[EDefaultVariableId.BrokerAddressNumber] = [legalContact.number];
    result[EDefaultVariableId.BrokerAddressBox] = [legalContact.box];
    result[EDefaultVariableId.BrokerAddressZip] = [legalContact.zip];
    result[EDefaultVariableId.BrokerAddressCity] = [legalContact.city];

    const country = translateByTemplateLanguage(
      `system.country.${legalContact.countryId}`.toLowerCase(),
    );
    result[EDefaultVariableId.BrokerAddressCountry] = [country];
  }

  if (broker) {
    result[EDefaultVariableId.BrokerOfficeName] = [broker.name];
    result[EDefaultVariableId.BrokerLogo] = [brokerLogoDocumentId];
  }

  if (meMaster) {
    result[EDefaultVariableId.BrokerSignature] = [meMaster.accountId];
  }

  // Owners
  const ownerAccounts = contract
    ? getMembersWithRole(
        contract?.members || [],
        EContractMemberTypes.Owner,
      ).map(owner => owner.account)
    : getMembersWithRole(
        property?.members || [],
        EPropertyMemberTypes.Owner,
      ).map(owner => owner.account);

  ownerAccounts.forEach(account => {
    const contact = contacts.find(contact =>
      contact.accountIds.includes(account.id),
    );

    const x = contact || account;

    result[EDefaultVariableId.OwnerIsCompany].push(!!x.company);
    result[EDefaultVariableId.OwnerFullName].push(
      [x.firstname, x.lastname].filter(Boolean).join(" "),
    );
    result[EDefaultVariableId.OwnerFirstName].push(x.firstname);
    result[EDefaultVariableId.OwnerLastName].push(x.lastname);
    result[EDefaultVariableId.OwnerBirthPlace].push(contact?.placeOfBirth);
    result[EDefaultVariableId.OwnerBirthDate].push(contact?.dateOfBirth);
    result[EDefaultVariableId.OwnerCompanyName].push(x.company);
    result[EDefaultVariableId.OwnerCompanyVat].push(contact?.VATNumber);
    result[EDefaultVariableId.OwnerCompanyRole].push(undefined);
    result[EDefaultVariableId.OwnerAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );
    result[EDefaultVariableId.OwnerAddressStreet].push(contact?.street);
    result[EDefaultVariableId.OwnerAddressNumber].push(contact?.number);
    result[EDefaultVariableId.OwnerAddressBox].push(contact?.box);
    result[EDefaultVariableId.OwnerAddressZip].push(contact?.zip);
    result[EDefaultVariableId.OwnerAddressCity].push(contact?.city);

    const country = contact
      ? translateByTemplateLanguage(
          `system.country.${contact.countryId}`.toLowerCase(),
        )
      : undefined;
    result[EDefaultVariableId.OwnerAddressCountry].push(country);
    result[EDefaultVariableId.OwnerCompanyAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );

    result[EDefaultVariableId.OwnerSignature].push(account.id);
    result[EDefaultVariableId.OwnerEmail].push(contact?.email);
    result[EDefaultVariableId.OwnerPhone].push(contact?.phone);
    result[EDefaultVariableId.OwnerNationality].push(
      contact?.nationality
        ? translateByTemplateLanguage(
            `system.nationality.${contact.nationality}`.toLowerCase(),
          )
        : undefined,
    );
    result[EDefaultVariableId.OwnerNationalRegisterNumber].push(
      contact?.nationalRegisterNumber,
    );
    result[EDefaultVariableId.OwnerPassportNumber].push(
      contact?.foreignIdentityNumber,
    );
  });

  result[EDefaultVariableId.OwnerBankAccountNumber].push(
    formatIban(ownerBankAccount?.iban),
  );

  // Tenants
  const tenantAccounts = getMembersWithOneOfRoles(contract?.members || [], [
    EContractMemberTypes.Tenant,
  ]).map(owner => owner.account);

  tenantAccounts.forEach(account => {
    const contact = contacts.find(contact =>
      contact.accountIds.includes(account.id),
    );

    const x = contact || account;

    result[EDefaultVariableId.TenantIsCompany].push(!!x.company);
    result[EDefaultVariableId.TenantFullName].push(
      [x.firstname, x.lastname].filter(Boolean).join(" "),
    );
    result[EDefaultVariableId.TenantFirstName].push(x.firstname);
    result[EDefaultVariableId.TenantLastName].push(x.lastname);
    result[EDefaultVariableId.TenantBirthPlace].push(contact?.placeOfBirth);
    result[EDefaultVariableId.TenantBirthDate].push(contact?.dateOfBirth);
    result[EDefaultVariableId.TenantCompanyName].push(x.company);
    result[EDefaultVariableId.TenantCompanyVat].push(contact?.VATNumber);
    result[EDefaultVariableId.TenantCompanyRole].push(undefined);
    result[EDefaultVariableId.TenantAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );
    result[EDefaultVariableId.TenantAddressStreet].push(contact?.street);
    result[EDefaultVariableId.TenantAddressNumber].push(contact?.number);
    result[EDefaultVariableId.TenantAddressBox].push(contact?.box);
    result[EDefaultVariableId.TenantAddressZip].push(contact?.zip);
    result[EDefaultVariableId.TenantAddressCity].push(contact?.city);
    result[EDefaultVariableId.TenantAddressCountry].push(contact?.countryId);
    result[EDefaultVariableId.TenantCompanyAddressFull].push(
      contact ? formatAddress(contact, true, true, true, true) : undefined,
    );
    result[EDefaultVariableId.TenantSignature].push(account.id);
    result[EDefaultVariableId.TenantEmail].push(contact?.email);
    result[EDefaultVariableId.TenantPhone].push(contact?.phone);
    result[EDefaultVariableId.TenantNationality].push(
      contact?.nationality
        ? translateByTemplateLanguage(
            `system.nationality.${contact.nationality}`.toLowerCase(),
          )
        : undefined,
    );
    result[EDefaultVariableId.TenantNationalRegisterNumber].push(
      contact?.nationalRegisterNumber,
    );
    result[EDefaultVariableId.TenantPassportNumber].push(
      contact?.foreignIdentityNumber,
    );
  });

  // Parents
  const parentAccounts = getMembersWithOneOfRoles(contract?.members || [], [
    EContractMemberTypes.Parent,
  ]).map(owner => owner.account);

  parentAccounts.forEach(account => {
    const contact = contacts.find(contact =>
      contact.accountIds.includes(account.id),
    );

    const x = contact || account;

    result[EDefaultVariableId.ParentIsCompany].push(!!x.company);
    result[EDefaultVariableId.ParentFullName].push(
      [x.firstname, x.lastname].filter(Boolean).join(" "),
    );
    result[EDefaultVariableId.ParentFirstName].push(x.firstname);
    result[EDefaultVariableId.ParentLastName].push(x.lastname);
    result[EDefaultVariableId.ParentBirthPlace].push(contact?.placeOfBirth);
    result[EDefaultVariableId.ParentBirthDate].push(contact?.dateOfBirth);
    result[EDefaultVariableId.ParentCompanyName].push(x.company);
    result[EDefaultVariableId.ParentCompanyVat].push(contact?.VATNumber);
    result[EDefaultVariableId.ParentCompanyRole].push(undefined);
    result[EDefaultVariableId.ParentAddressFull].push(
      contact ? formatAddress(contact) : undefined,
    );
    result[EDefaultVariableId.ParentAddressStreet].push(contact?.street);
    result[EDefaultVariableId.ParentAddressNumber].push(contact?.number);
    result[EDefaultVariableId.ParentAddressBox].push(contact?.box);
    result[EDefaultVariableId.ParentAddressZip].push(contact?.zip);
    result[EDefaultVariableId.ParentAddressCity].push(contact?.city);
    result[EDefaultVariableId.ParentAddressCountry].push(contact?.countryId);
    result[EDefaultVariableId.ParentCompanyAddressFull].push(
      contact ? formatAddress(contact) : undefined,
    );
    result[EDefaultVariableId.ParentSignature].push(account.id);
    result[EDefaultVariableId.ParentEmail].push(contact?.email);
    result[EDefaultVariableId.ParentPhone].push(contact?.phone);
    result[EDefaultVariableId.ParentNationality].push(
      contact?.nationality
        ? translateByTemplateLanguage(
            `system.nationality.${contact.nationality}`.toLowerCase(),
          )
        : undefined,
    );
    result[EDefaultVariableId.ParentNationalRegisterNumber].push(
      contact?.nationalRegisterNumber,
    );
    result[EDefaultVariableId.ParentPassportNumber].push(
      contact?.foreignIdentityNumber,
    );
  });

  // Property
  const propertyType = PROPERTY_TYPES.find(type => type.id === property.typeId);
  result[EDefaultVariableId.PropertyType].push(
    propertyType
      ? translateByTemplateLanguage(`property.type.${propertyType.label}`)
      : undefined,
  );

  result[EDefaultVariableId.PropertyAddressFull].push(
    formatAddress(property, true, true, true, true),
  );
  result[EDefaultVariableId.PropertyAddressStreet].push(property.street);
  result[EDefaultVariableId.PropertyAddressNumber].push(property.number);
  result[EDefaultVariableId.PropertyAddressBox].push(property.box);
  result[EDefaultVariableId.PropertyAddressZip].push(property.zip);
  result[EDefaultVariableId.PropertyAddressCity].push(property.city);
  result[EDefaultVariableId.PropertyName].push(property.name);
  result[EDefaultVariableId.PropertyReference].push(property.reference);
  result[EDefaultVariableId.EanCodeElectricity].push(property.electricityEan);
  result[EDefaultVariableId.EanCodeGas].push(property.naturalGasEan);
  result[EDefaultVariableId.MeterNumberGas].push(
    property.naturalGasMeterNumber,
  );
  result[EDefaultVariableId.MeterNumberElectricity].push(
    property.electricityMeterNumberBoth,
  );
  result[EDefaultVariableId.MeterNumberWater].push(property.waterMeterNumber);

  const { epcLabel } = property;

  result[EDefaultVariableId.PropertyEpcLabel].push(
    translateByTemplateLanguage(
      `epc.label.${(epcLabel || EPropertyEPCLabel.Unknown).toLowerCase()}`,
    ),
  );

  const hasEpc =
    epcLabel &&
    ![EPropertyEPCLabel.Unknown, EPropertyEPCLabel.NoEPCAvailable].includes(
      epcLabel,
    );
  if (hasEpc) {
    result[EDefaultVariableId.PropertyEpcValue].push(property.epcValue);
    result[EDefaultVariableId.PropertyEpcExpirationDate].push(
      property.epcExpirationDate,
    );
  } else {
    result[EDefaultVariableId.PropertyEpcValue].push(undefined);
    result[EDefaultVariableId.PropertyEpcExpirationDate].push(undefined);
  }

  const country = translateByTemplateLanguage(
    `system.country.${property.countryId || "BE"}`.toLowerCase(),
  );
  result[EDefaultVariableId.PropertyAddressCountry].push(country);

  const technicalManagers = getMembersWithRole(
    property?.members || [],
    EPropertyMemberTypes.TechnicalManager,
  )
    .map(member => member.account)
    .map(account =>
      contacts.find(contact => contact.accountIds.includes(account.id)),
    )
    .filter(Boolean)
    .map(x => (x ? getName(x) : ""))
    .join(", ");

  const financialManagers = getMembersWithRole(
    property?.members || [],
    EPropertyMemberTypes.FinancialManager,
  )
    .map(member => member.account)
    .map(account =>
      contacts.find(contact => contact.accountIds.includes(account.id)),
    )
    .filter(Boolean)
    .map(x => (x ? getName(x) : ""))
    .join(", ");

  result[EDefaultVariableId.PropertyTechnicalManagers].push(technicalManagers);
  result[EDefaultVariableId.PropertyFinancialManagers].push(financialManagers);

  result[EDefaultVariableId.PropertyConstructionYear].push(
    property.constructionYear,
  );

  result[EDefaultVariableId.PropertyElectricityType].push(
    property.electricityType
      ? translateByTemplateLanguage(
          `move.option.electricity_type.${property.electricityType}`.toLowerCase(),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyElectricityMeterType].push(
    property.electricityMeterType
      ? translateByTemplateLanguage(
          `move.option.electricity_meter_type.${property.electricityMeterType}`.toLowerCase(),
        )
      : undefined,
  );

  result[EDefaultVariableId.PropertyNaturalGasAvailable].push(
    property.naturalGasAvailable
      ? translateByTemplateLanguage(
          `move.option.natural_gas_type.${property.naturalGasAvailable}`.toLowerCase(),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyNaturalGasMeterType].push(
    property.naturalGasMeterType
      ? translateByTemplateLanguage(
          `move.option.natural_gas_meter_type.${property.naturalGasMeterType}`.toLowerCase(),
        )
      : undefined,
  );

  result[EDefaultVariableId.PropertyEpcCertificateId].push(
    property.epcCertificateId,
  );

  // Exterior - from propertyAmenities
  const buildingAdjoined = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.buildingAdjoined,
  );
  result[EDefaultVariableId.PropertyBuildingAdjoined].push(
    buildingAdjoined
      ? translateByTemplateLanguage(
          `property.adjoined.option.${buildingAdjoined}.label`.toLowerCase(),
        )
      : undefined,
  );
  result[EDefaultVariableId.PropertyBuildingSurfaceArea].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.buildingSurfaceArea),
  );
  result[EDefaultVariableId.PropertyBuildingGardenSurfaceArea].push(
    property.buildingGardenSurfaceArea,
  );
  result[EDefaultVariableId.PropertyBuildingTerraceSurfaceArea].push(
    property.buildingTerraceSurfaceArea,
  );

  // Interior - from propertyAmenities
  result[EDefaultVariableId.PropertyBuildingFloor].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.buildingFloor),
  );
  result[EDefaultVariableId.PropertyBuildingFloorCount].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.buildingFloorCount),
  );
  result[EDefaultVariableId.PropertyRoomCountBedroom].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountBedroom),
  );
  result[EDefaultVariableId.PropertyRoomCountBathroom].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountBathroom),
  );
  result[EDefaultVariableId.PropertyRoomCountGarage].push(
    getAmenityNumberValue(propertyAmenities, AMENITY_NAMES.roomCountGarage),
  );
  result[EDefaultVariableId.PropertyBuildingHasStorage].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.buildingHasStorage),
  );
  result[EDefaultVariableId.PropertyIsFurnished].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.isFurnished),
  );

  // Amenities - from propertyAmenities
  result[EDefaultVariableId.PropertyHasOven].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasOven),
  );
  result[EDefaultVariableId.PropertyHasFridge].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasFridge),
  );
  result[EDefaultVariableId.PropertyHasDishwasher].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDishwasher),
  );
  result[EDefaultVariableId.PropertyHasRangeHood].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasRangeHood),
  );
  result[EDefaultVariableId.PropertyHasLift].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasLift),
  );

  // Energy - from propertyAmenities
  result[EDefaultVariableId.PropertyHasDoubleGlazing].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasDoubleGlazing),
  );
  result[EDefaultVariableId.PropertyHasCentralHeating].push(
    getAmenityBooleanValue(propertyAmenities, AMENITY_NAMES.hasCentralHeating),
  );
  const heatingType = getAmenityStringValue(
    propertyAmenities,
    AMENITY_NAMES.heatingType,
  );
  result[EDefaultVariableId.PropertyHeatingType].push(
    heatingType
      ? translateByTemplateLanguage(
          `property.extra.option.energy.${heatingType}`.toLowerCase(),
        )
      : undefined,
  );

  if (contract) {
    result[EDefaultVariableId.RentDepositPrice].push(
      contract?.rentDepositAmount,
    );
    const rentDepositMonths =
      (contract.rentDepositAmount || 0) /
      getAmountWithVat(
        contract.currentPrice || 0,
        (contract.priceVat || 0) / 100,
      );
    if (round(rentDepositMonths) === rentDepositMonths) {
      result[EDefaultVariableId.RentDepositMonths].push(rentDepositMonths);
    }

    result[EDefaultVariableId.ContractStartDate].push(contract.startDate);
    result[EDefaultVariableId.ContractStopDate].push(contract.stopDate);
    result[EDefaultVariableId.ContractPaymentRepetition].push(
      translateByTemplateLanguage(
        `contract.payment_repetition.option.${contract.repetitionType}`.toLowerCase(),
      ).toLowerCase(),
    );

    result[EDefaultVariableId.ContractPriceVat].push(contract.priceVat);
    result[EDefaultVariableId.ContractStartPrice].push(contract.startPrice);
    result[EDefaultVariableId.ContractStartPriceIncludingVat].push(
      getAmountWithVat(contract.startPrice, (contract.priceVat || 0) / 100),
    );

    result[EDefaultVariableId.ContractCostsCommonVat].push(
      contract.costsCommonVat,
    );
    result[EDefaultVariableId.ContractCostsNonCommonVat].push(
      contract.costsNonCommonVat,
    );

    result[EDefaultVariableId.ContractStartCostsCommonFixed].push(
      getAmountWithVat(
        contract.startCostsCommon,
        (contract.costsCommonVat || 0) / 100,
      ),
    );
    result[EDefaultVariableId.ContractStartCostsCommonFixedIncludingVat].push(
      contract.startCostsCommon,
    );
    result[EDefaultVariableId.ContractStartCostsCommonProvision].push(
      contract.startCostsCommon,
    );
    result[
      EDefaultVariableId.ContractStartCostsCommonProvisionIncludingVat
    ].push(
      getAmountWithVat(
        contract.startCostsCommon,
        (contract.costsCommonVat || 0) / 100,
      ),
    );

    result[EDefaultVariableId.ContractStartCostsNonCommonFixed].push(
      contract.startCostsNonCommon,
    );
    result[
      EDefaultVariableId.ContractStartCostsNonCommonFixedIncludingVat
    ].push(
      round(
        contract.startCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );
    result[EDefaultVariableId.ContractStartCostsNonCommonProvision].push(
      contract.startCostsNonCommon,
    );
    result[
      EDefaultVariableId.ContractStartCostsNonCommonProvisionIncludingVat
    ].push(
      round(
        contract.startCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );

    result[EDefaultVariableId.ContractCurrentPrice].push(contract.currentPrice);
    result[EDefaultVariableId.ContractCurrentPriceIncludingVat].push(
      getAmountWithVat(contract.currentPrice, (contract.priceVat || 0) / 100),
    );

    result[EDefaultVariableId.ContractCurrentCostsCommonFixed].push(
      contract.currentCostsCommon,
    );
    result[EDefaultVariableId.ContractCurrentCostsCommonFixedIncludingVat].push(
      round(
        contract.currentCostsCommon *
          (1 + (contract.costsCommonVat || 0) / 100),
        2,
      ),
    );
    result[EDefaultVariableId.ContractCurrentCostsCommonProvision].push(
      contract.currentCostsCommon,
    );
    result[
      EDefaultVariableId.ContractCurrentCostsCommonProvisionIncludingVat
    ].push(
      round(
        contract.currentCostsCommon *
          (1 + (contract.costsCommonVat || 0) / 100),
        2,
      ),
    );

    result[EDefaultVariableId.ContractCurrentCostsNonCommonFixed].push(
      contract.currentCostsNonCommon,
    );
    result[
      EDefaultVariableId.ContractCurrentCostsNonCommonFixedIncludingVat
    ].push(
      round(
        contract.currentCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );
    result[EDefaultVariableId.ContractCurrentCostsNonCommonProvision].push(
      contract.costsNonCommonVat,
    );
    result[
      EDefaultVariableId.ContractCurrentCostsNonCommonProvisionIncludingVat
    ].push(
      round(
        contract.currentCostsNonCommon *
          (1 + (contract.costsNonCommonVat || 0) / 100),
        2,
      ),
    );

    const {
      priceTypeCommonCosts = EContractCostsPriceType.Fixed,
      priceTypeNonCommonCosts = EContractCostsPriceType.Provision,
    } = contract;

    const priceTypeCostsCommon =
      contract.currentCostsCommon > 0
        ? translateByTemplateLanguage(
            `contract_info.costs.price_type.value.${priceTypeCommonCosts.toLowerCase()}`,
          )
        : translateByTemplateLanguage(
            "contract_info.costs.price_type.value.none",
          );
    result[EDefaultVariableId.ContractPriceTypeCostsCommon].push(
      priceTypeCostsCommon,
    );
    const priceTypeCostsNonCommon =
      contract.currentCostsNonCommon > 0
        ? translateByTemplateLanguage(
            `contract_info.costs.price_type.value.${priceTypeNonCommonCosts.toLowerCase()}`,
          )
        : translateByTemplateLanguage(
            "contract_info.costs.price_type.value.none",
          );

    result[EDefaultVariableId.ContractPriceTypeCostsNonCommon].push(
      priceTypeCostsNonCommon,
    );
    result[EDefaultVariableId.ContractStartIndexDate].push(
      new Date(
        contract.startIndexYear,
        contract.startIndexMonth - 1, // Zero-based
        1,
      ),
    );
    // Get start index
    result[EDefaultVariableId.ContractStartIndexNumber].push(
      undefined,
      // contract.startIndexId,
    );
    result[EDefaultVariableId.ContractDurationYears].push(
      contract.stopDate
        ? floor(
            differenceInDays(
              addDays(contract.stopDate, 1),
              contract.startDate,
            ) / 365,
          )
        : undefined,
    );

    if (indexInfoAvailable && indexInfoSimulation && indexInfoRequest) {
      const epcLabel = property.epcLabel || EPropertyEPCLabel.Unknown;

      // New price as filled in by the user
      const newPriceWithVat = getAmountWithVat(
        indexInfoRequest.newPrice,
        (contract.priceVat || 0) / 100,
      );

      const increaseRatio = indexInfoRequest.newPrice / contract.currentPrice;
      const newCommonCosts = contract.commonCostsAreIndexable
        ? contract.currentCostsCommon * increaseRatio
        : contract.currentCostsCommon;
      const newCommonCostsWithVat = getAmountWithVat(
        newCommonCosts,
        (contract.priceVat || 0) / 100,
      );
      const newNonCommonCosts = contract.nonCommonCostsAreIndexable
        ? contract.currentCostsNonCommon * increaseRatio
        : contract.currentCostsNonCommon;
      const newNonCommonCostsWithVat = getAmountWithVat(
        newNonCommonCosts,
        (contract.priceVat || 0) / 100,
      );

      // New price as proposed by Rentio (max. allowed increase)
      let proposedNewPrice = indexInfoAvailable.newPrice;
      let proposedCorrectionFactor = 1;
      if (
        indexInfoAvailable.epcBasedIndexationType ===
          EEpcBasedIndexationType.Corrected &&
        !indexInfoRequest.forceFullIndexation
      ) {
        if (epcLabel === EPropertyEPCLabel.D) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcD;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcD;
        } else if (epcLabel === EPropertyEPCLabel.E) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcE;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcE;
        } else if (epcLabel === EPropertyEPCLabel.F) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcF;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcF;
        } else if (epcLabel === EPropertyEPCLabel.G) {
          proposedNewPrice = indexInfoAvailable.newPriceEpcG;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorEpcG;
        } else if (
          epcLabel === EPropertyEPCLabel.NoEPCAvailable ||
          epcLabel === EPropertyEPCLabel.Unknown
        ) {
          proposedNewPrice = indexInfoAvailable.newPriceNoEpc;
          proposedCorrectionFactor = indexInfoAvailable.correctionFactorNoEpc;
        }
      }

      const proposedNewPriceWithVat = getAmountWithVat(
        proposedNewPrice,
        (contract.priceVat || 0) / 100,
      );

      const proposedIncreaseRatio = proposedNewPrice / contract.currentPrice;
      const proposedNewCommonCosts = contract.commonCostsAreIndexable
        ? contract.currentCostsCommon * proposedIncreaseRatio
        : contract.currentCostsCommon;
      const proposedNewCommonCostsWithVat = getAmountWithVat(
        proposedNewCommonCosts,
        (contract.priceVat || 0) / 100,
      );
      const proposedNewNonCommonCosts = contract.nonCommonCostsAreIndexable
        ? contract.currentCostsNonCommon * proposedIncreaseRatio
        : contract.currentCostsNonCommon;
      const proposedNewNonCommonCostsWithVat = getAmountWithVat(
        proposedNewNonCommonCosts,
        (contract.priceVat || 0) / 100,
      );

      result[EDefaultVariableId.ContractIndexStartIndex].push(
        indexInfoAvailable.startIndex,
      );
      result[EDefaultVariableId.ContractIndexNewIndex].push(
        indexInfoAvailable.newIndex,
      );
      result[EDefaultVariableId.ContractIndexCorrectionFactor].push(
        proposedCorrectionFactor !== 1 ? proposedCorrectionFactor : undefined,
      );
      result[EDefaultVariableId.ContractIndexEpcBasedIndexType].push(
        indexInfoAvailable.epcBasedIndexationType,
      );

      result[EDefaultVariableId.ContractIndexNewPriceWasAdjusted].push(
        proposedNewPrice !== indexInfoRequest.newPrice,
      );

      result[EDefaultVariableId.ContractIndexNewPriceOriginal].push(
        round(proposedNewPriceWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewCommonCostsOriginal].push(
        round(proposedNewCommonCostsWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsOriginal].push(
        round(proposedNewNonCommonCostsWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexNewPriceAdjusted].push(
        round(newPriceWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewCommonCostsAdjusted].push(
        round(newCommonCostsWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsAdjusted].push(
        round(newNonCommonCostsWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexTotalPriceOriginal].push(
        proposedNewPriceWithVat +
          getAmountWithVat(
            contract.currentCostsCommon,
            (contract.costsCommonVat || 0) / 100,
          ) +
          getAmountWithVat(
            contract.currentCostsNonCommon,
            (contract.costsNonCommonVat || 0) / 100,
          ),
      );
      result[EDefaultVariableId.ContractIndexTotalPriceAdjusted].push(
        newPriceWithVat +
          contract.currentCostsNonCommon +
          contract.currentCostsCommon,
      );

      result[EDefaultVariableId.ContractIndexForceFullIndexation].push(
        indexInfoRequest.forceFullIndexation,
      );

      const startNextPaymentPeriod =
        indexInfoAvailable.retroactivityPeriodStart || new Date();
      result[EDefaultVariableId.ContractIndexStartNextPaymentPeriod].push(
        startNextPaymentPeriod,
      );

      const now = new Date();

      result[EDefaultVariableId.ContractIndexRetroactivePayments].push(
        indexInfoRequest.isRetroactive,
      );

      result[EDefaultVariableId.ContractIndexCorrectionPeriodStart].push(
        indexInfoSimulation.correctionPeriodStart,
      );

      result[EDefaultVariableId.ContractIndexCorrectionPeriodEnd].push(
        indexInfoSimulation.correctionPeriodEnd,
      );

      result[EDefaultVariableId.ContractRetroactivityPeriodStart].push(
        indexInfoSimulation.retroactivityPeriodStart,
      );

      result[EDefaultVariableId.ContractRetroactivityPeriodEnd].push(
        indexInfoSimulation.retroactivityPeriodEnd,
      );

      const retroactivityPriceWithVat = getAmountWithVat(
        indexInfoSimulation.retroactivityPrice,
        (contract.priceVat || 0) / 100,
      );
      const correctionPriceWithVat = getAmountWithVat(
        indexInfoSimulation.correctionPrice,
        (contract.priceVat || 0) / 100,
      );

      result[EDefaultVariableId.ContractRetroactivityPeriodPrice].push(
        round(retroactivityPriceWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexCorrectionPeriodPrice].push(
        round(correctionPriceWithVat, 2),
      );
      result[EDefaultVariableId.ContractIndexCorrectionAndRetroSum].push(
        round(retroactivityPriceWithVat + correctionPriceWithVat, 2),
      );

      result[EDefaultVariableId.ContractIndexPaymentsWithRentio].push(
        !!rentPaymentOrder,
      );

      const day = new Date(
        indexInfoSimulation.retroactivityPeriodStart ||
          indexInfoSimulation.correctionPeriodStart,
      ).getDate();

      const indexationStartAt = new Date(
        now.getFullYear(),
        now.getMonth(),
        day,
      );

      result[EDefaultVariableId.ContractIndexPaymentOrderNewDueDate].push(
        indexationStartAt,
      );
    } else {
      result[EDefaultVariableId.ContractIndexStartIndex].push(undefined);
      result[EDefaultVariableId.ContractIndexNewIndex].push(undefined);
      result[EDefaultVariableId.ContractIndexCorrectionFactor].push(undefined);
      result[EDefaultVariableId.ContractIndexEpcBasedIndexType].push(undefined);
      result[EDefaultVariableId.ContractIndexNewPriceWasAdjusted].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewPriceOriginal].push(undefined);
      result[EDefaultVariableId.ContractIndexNewCommonCostsOriginal].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsOriginal].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewPriceAdjusted].push(undefined);
      result[EDefaultVariableId.ContractIndexNewCommonCostsAdjusted].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexNewNonCommonCostsAdjusted].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexForceFullIndexation].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexStartNextPaymentPeriod].push(
        undefined,
      );
      result[EDefaultVariableId.ContractRetroactivityPeriodStart].push(
        undefined,
      );
      result[EDefaultVariableId.ContractRetroactivityPeriodEnd].push(undefined);
      result[EDefaultVariableId.ContractRetroactivityPeriodPrice].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexPaymentsWithRentio].push(
        undefined,
      );
      result[EDefaultVariableId.ContractIndexPaymentOrderNewDueDate].push(
        undefined,
      );
    }

    if (
      contract.ibanisationStatus === EContractIbanisationStatus.Enabled &&
      ibanisationBankAccount
    ) {
      result[EDefaultVariableId.ContractIbanisationBankAccountIban].push(
        formatIban(ibanisationBankAccount.iban),
      );
      result[EDefaultVariableId.ContractIbanisationBankAccountBic].push(
        ibanisationBankAccount.mangoWalletIbanBic,
      );
      result[EDefaultVariableId.ContractIbanisationBankAccountReference].push(
        contract.ibanisationReference,
      );
    }
  }

  // Additional costs
  const indexableCosts =
    contract?.additionalCosts?.filter(cost => cost?.isIndexable) || [];
  if (contract && indexableCosts?.length) {
    const increaseRatio = (
      contract?.currentPrice / contract?.startPrice
    ).toFixed(2);

    indexableCosts.forEach(cost => {
      const { currentPrice = 0, startPrice = 0, description } = cost;
      const appliedIncrease = round(currentPrice - startPrice, 2);

      result[EDefaultVariableId.AdditionalCostDescription].push(description);
      result[EDefaultVariableId.AdditionalCostStartPrice].push(startPrice);
      result[EDefaultVariableId.AdditionalCostCorrectionFactor].push(
        increaseRatio,
      );
      result[EDefaultVariableId.AdditionalCostProposedIncrease].push(
        appliedIncrease,
      );
      result[EDefaultVariableId.AdditionalCostNewPrice].push(currentPrice);
    });
  }

  Object.keys(preferences).forEach(preferenceKey => {
    if (!preferenceKey.startsWith("template_document:field:")) {
      return;
    }

    const variableId = preferenceKey.replace("template_document:field:", "");
    const variable = fields.variables.find(x => x.id === variableId);
    if (!variable?.persisted) {
      return;
    }

    const data = preferences[preferenceKey];
    if (!data || !Array.isArray(data)) {
      return;
    }

    // @ts-ignore
    result[variableId] = data;
  });

  return JSON.stringify({
    ...result,
    ...variablesDataToOverride,
  });
};

export const contactIsValidForTemplate = (contact: IContact) => {
  if (!contact.placeOfBirth) {
    return false;
  }

  if (!contact.dateOfBirth) {
    return false;
  }

  if (!contact.nationality) {
    return false;
  }

  if (contact.nationality !== "BE") {
    if (!contact.nationalRegisterNumber && !contact.foreignIdentityNumber) {
      return false;
    }
  }

  if (contact.nationality === "BE" && !contact.nationalRegisterNumber) {
    return false;
  }

  return true;
};
